// Audio upload helper with progress + retry integration.
// XHR instead of fetch because fetch() has no upload-progress event.

import { ApiClientError, getToken } from '@/lib/api'
import type { UploadAudioResponse } from '@yui-receipt/shared'

const BASE = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '') + '/api'

export type UploadProgress = {
  /** 0..1 */
  ratio: number
  /** bytes uploaded so far */
  loaded: number
  /** total bytes (may be 0 if not computable) */
  total: number
}

export type UploadAudioParams = {
  visitId: number
  blob: Blob
  mimeType: string
  filename?: string
  signal?: AbortSignal
  onProgress?: (p: UploadProgress) => void
}

export async function uploadAudio({
  visitId,
  blob,
  mimeType,
  filename,
  signal,
  onProgress,
}: UploadAudioParams): Promise<UploadAudioResponse> {
  const url = `${BASE}/visits/${visitId}/upload-audio`
  const form = new FormData()
  const name = filename ?? inferFilename(mimeType)
  form.append('audio', new File([blob], name, { type: mimeType }))

  return new Promise<UploadAudioResponse>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', url, true)
    const token = getToken()
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.withCredentials = true

    if (onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        onProgress({
          ratio: e.lengthComputable ? e.loaded / e.total : 0,
          loaded: e.loaded,
          total: e.lengthComputable ? e.total : 0,
        })
      })
    }

    xhr.addEventListener('load', () => {
      const status = xhr.status
      const ct = xhr.getResponseHeader('content-type') ?? ''
      const body = ct.includes('application/json') && xhr.responseText
        ? safeJson(xhr.responseText)
        : null
      if (status >= 200 && status < 300) {
        resolve(body as UploadAudioResponse)
      } else {
        const err = (body as { error?: { code?: string; message?: string; details?: Record<string, unknown> } } | null)?.error
        reject(
          new ApiClientError(
            status,
            err?.code ?? `HTTP_${status}`,
            err?.message ?? xhr.statusText ?? 'Upload failed',
            err?.details,
          ),
        )
      }
    })
    xhr.addEventListener('error', () =>
      reject(new ApiClientError(0, 'NETWORK_ERROR', 'เครือข่ายขัดข้อง')),
    )
    xhr.addEventListener('abort', () =>
      reject(new ApiClientError(0, 'ABORTED', 'การอัปโหลดถูกยกเลิก')),
    )

    if (signal) {
      if (signal.aborted) {
        xhr.abort()
      } else {
        signal.addEventListener('abort', () => xhr.abort(), { once: true })
      }
    }

    xhr.send(form)
  })
}

function inferFilename(mime: string): string {
  if (mime.includes('webm')) return 'audio.webm'
  if (mime.includes('mp4')) return 'audio.m4a'
  if (mime.includes('ogg')) return 'audio.ogg'
  if (mime.includes('mpeg')) return 'audio.mp3'
  return 'audio.bin'
}

function safeJson(s: string): unknown {
  try {
    return JSON.parse(s)
  } catch {
    return null
  }
}

/** Classify an upload failure as retryable vs permanent. */
export function isRetryableError(err: unknown): boolean {
  if (err instanceof ApiClientError) {
    // 0 = network, 408 = timeout, 429 = rate limit, 5xx = server
    if (err.status === 0) return err.code !== 'ABORTED'
    if (err.status === 408 || err.status === 429) return true
    if (err.status >= 500) return true
    return false
  }
  // Unknown error — try once more.
  return true
}
